import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sparkles,
  Lock,
  ExternalLink,
  TrendingDown,
  Info,
  Wallet,
  Rocket,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const formatCurrency = (n) =>
  `$${(Number(n) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

export default function CoOpBalanceCard() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/dashboard/co-op-balance`, {
          headers: getAuthHeader(),
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error("Failed to fetch co-op balance:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div
        data-testid="co-op-balance-loading"
        className="bg-[#0F172A] border border-white/10 rounded-2xl p-6 h-40 animate-pulse"
      />
    );
  }

  if (!data) return null;

  const {
    is_best_of_best,
    balance,
    initial_amount,
    monthly_deduction,
    next_deduction_at,
    days_until_next_deduction,
    cycle_position,
    cycle_number,
    platform_items = [],
    platform_total_value,
    apply_url,
  } = data;

  const progressPct = is_best_of_best
    ? Math.max(0, Math.min(100, (balance / initial_amount) * 100))
    : 0;

  const visibleItems = showAll ? platform_items : platform_items.slice(0, 6);

  return (
    <motion.div
      data-testid="co-op-balance-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`relative overflow-hidden rounded-2xl border ${
        is_best_of_best
          ? "border-[#F59E0B]/40 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]"
          : "border-white/10 bg-[#0F172A]"
      }`}
    >
      {/* Decorative gold shimmer for BoB users */}
      {is_best_of_best && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(600px circle at 15% 0%, rgba(245,158,11,0.25), transparent 60%), radial-gradient(500px circle at 100% 100%, rgba(124,58,237,0.18), transparent 60%)",
          }}
        />
      )}

      <div className="relative p-6 sm:p-8">
        {/* Header row */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-start gap-3">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                is_best_of_best
                  ? "bg-gradient-to-br from-[#F59E0B] to-[#EC4899]"
                  : "bg-white/5"
              }`}
            >
              {is_best_of_best ? (
                <Wallet className="w-6 h-6 text-white" />
              ) : (
                <Lock className="w-5 h-5 text-[#94A3B8]" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg md:text-xl font-bold text-white font-['Outfit']">
                  Co-Op Balance
                </h2>
                {is_best_of_best && (
                  <span
                    data-testid="co-op-bob-badge"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#EC4899] text-white"
                  >
                    <Sparkles className="w-3 h-3" /> Best of Best
                  </span>
                )}
              </div>
              <p className="text-sm text-[#94A3B8] mt-1">
                {is_best_of_best
                  ? `Your private $${(initial_amount / 1000).toFixed(0)}k/yr Co-Op Fund covers your AI Horse-Powered platform stack.`
                  : "Unlock $120,000/year in Co-Op Marketing Support when you become a Best of Best member."}
              </p>
            </div>
          </div>

          <div className="text-left md:text-right">
            <div
              data-testid="co-op-balance-amount"
              className={`text-3xl md:text-4xl font-black tracking-tight ${
                is_best_of_best ? "text-white" : "text-[#64748B]"
              }`}
            >
              {formatCurrency(balance)}
            </div>
            <p className="text-xs text-[#64748B] mt-1">
              {is_best_of_best
                ? `Cycle ${cycle_number} · ${cycle_position} of 12 deductions applied`
                : "Locked — Not Best of Best"}
            </p>
          </div>
        </div>

        {/* Progress bar + next deduction (BoB only) */}
        {is_best_of_best && (
          <div className="mb-6">
            <div className="h-3 bg-white/5 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="h-full bg-gradient-to-r from-[#F59E0B] via-[#EC4899] to-[#7C3AED] rounded-full"
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3 text-sm">
              <div className="flex items-center gap-2 text-[#94A3B8]">
                <TrendingDown className="w-4 h-4 text-[#F59E0B]" />
                <span>
                  Next deduction of{" "}
                  <span className="text-white font-semibold">
                    {formatCurrency(monthly_deduction)}
                  </span>{" "}
                  on{" "}
                  <span
                    data-testid="co-op-next-deduction-date"
                    className="text-white font-semibold"
                  >
                    {formatDate(next_deduction_at)}
                  </span>{" "}
                  ({days_until_next_deduction} days)
                </span>
              </div>
              <div className="text-xs text-[#64748B] flex items-center gap-1">
                <Info className="w-3 h-3" />
                Balance auto-reloads to {formatCurrency(initial_amount)} on cycle
                end.
              </div>
            </div>
          </div>
        )}

        {/* Non-BoB CTA */}
        {!is_best_of_best && (
          <div
            data-testid="co-op-apply-cta"
            className="mb-6 rounded-xl border border-[#F59E0B]/30 bg-gradient-to-r from-[#F59E0B]/10 via-[#EC4899]/10 to-[#7C3AED]/10 p-5"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-white font-semibold text-base md:text-lg">
                  Want to become Best of Best and Get $120k in Co-Op Funding?
                </p>
                <p className="text-sm text-[#94A3B8] mt-1">
                  Only takes 3 minutes. $75 application fee — 100% refundable if
                  you're not approved.
                </p>
              </div>
              <a
                href={apply_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="co-op-apply-now-btn"
              >
                <Button className="whitespace-nowrap bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:opacity-90 text-white font-semibold px-5 py-6 rounded-xl shadow-lg shadow-[#EC4899]/20">
                  <Rocket className="w-4 h-4 mr-2" />
                  Apply Now to Get $120k Co-Op
                  <ExternalLink className="w-4 h-4 ml-2 opacity-80" />
                </Button>
              </a>
            </div>
          </div>
        )}

        {/* Explainer: what the $10k/month covers */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              What Your{" "}
              <span className="text-[#F59E0B]">
                {formatCurrency(monthly_deduction)}/month
              </span>{" "}
              Covers
            </h3>
            <span className="text-xs text-[#64748B]">
              Retail value:{" "}
              <span className="text-white font-semibold">
                ${platform_total_value?.toLocaleString?.() || "11,997"}/mo
              </span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {visibleItems.map((item, i) => (
              <div
                key={item.name}
                data-testid={`co-op-platform-item-${i}`}
                className={`flex items-start gap-3 p-3 rounded-lg border ${
                  is_best_of_best
                    ? "border-white/10 bg-white/5"
                    : "border-white/5 bg-white/[0.02] opacity-70"
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center text-[11px] font-bold ${
                    is_best_of_best
                      ? "bg-gradient-to-br from-[#F59E0B]/30 to-[#EC4899]/30 text-[#F59E0B]"
                      : "bg-white/5 text-[#64748B]"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-white truncate">
                      {item.name}
                    </p>
                    <span className="text-xs font-bold text-[#F59E0B] whitespace-nowrap">
                      ${item.price.toLocaleString()}/mo
                    </span>
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-0.5 leading-snug">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {platform_items.length > 6 && (
            <button
              data-testid="co-op-toggle-items-btn"
              onClick={() => setShowAll((v) => !v)}
              className="mt-3 text-sm text-[#F59E0B] hover:text-[#EC4899] font-semibold transition-colors"
            >
              {showAll
                ? "Show less"
                : `Show all ${platform_items.length} platforms & services →`}
            </button>
          )}

          {!is_best_of_best && (
            <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 border-t border-white/5">
              <p className="text-xs text-[#64748B]">
                Locked until you're approved as a Best of Best member in your
                category & market.
              </p>
              <a
                href={apply_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="co-op-apply-inline-link"
                className="text-sm font-semibold text-[#F59E0B] hover:text-[#EC4899] inline-flex items-center gap-1"
              >
                Apply Now — 3 min
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
